import { setNonce } from "get-nonce";

/**
 * The literal that the EDGE nginx rewrites via sub_filter — see
 * apps/web/index.html and infra/compose/nginx/prod.conf. Nothing in the app
 * substitutes it, deliberately.
 */
const PLACEHOLDER = "__CSP_NONCE__";

/**
 * Publish the edge-minted per-request CSP nonce to `get-nonce`'s module global,
 * then REMOVE the tag that carried it (CHP-1337, ADR-0053).
 *
 * Must run BEFORE the first render. `react-style-singleton` — the injector
 * behind Radix Dialog/Sheet scroll-lock, and Select — reads the nonce from that
 * global at style-tag-creation time rather than from React context, so a nonce
 * published after the first `<style>` is injected arrives too late and that
 * stylesheet is blocked by `style-src-elem`.
 *
 * WHY THE TAG IS REMOVED, not just read. Browsers deliberately hide the `nonce`
 * content attribute of `<script>`/`<style>` behind an internal slot so that
 * injected CSS cannot read it back with an attribute selector. A `<meta>` tag
 * defeats that: it leaves the live nonce readable by exactly the
 * attribute-selector + `background-image` exfiltration this ticket exists to
 * block, and Sentry's Replay integration serialises element attributes verbatim
 * (its only exclusion is `autoplay` on media), bypassing `beforeSend` — so an
 * error would upload the nonce off-box. Call this before Sentry.init: rrweb
 * takes its first full snapshot synchronously inside it, because a deferred
 * module means readyState is already "interactive".
 *
 * The tag is still live from HTML parse until this module evaluates, and that
 * window scales with bundle download rather than being "a few milliseconds".
 * What makes it safe is not its length: no attacker-controlled content exists
 * in the document yet, since every injection surface in the threat model
 * arrives from the API after first render. Note also that `element.nonce`
 * stays readable to same-origin script on the injected tags — browsers blank
 * the content attribute but keep the IDL property — so this closes the CSS
 * attribute-selector path, not script access, which `script-src 'self'`
 * already governs.
 *
 * Returns the nonce it applied, or `undefined` when there is nothing to apply.
 * Undefined is the NORMAL case on the laptop compose stack and under Vite dev:
 * neither substitutes the placeholder, and neither serves a CSP either, so an
 * unnonced `<style>` is not blocked there.
 */
export function installCspNonce(): string | undefined {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="csp-nonce"]');
  const nonce = meta?.content;

  // Remove it whether or not it was substituted: an unsubstituted placeholder
  // is no use to anyone either, and leaving it behind invites someone to read
  // the nonce from the DOM instead of from get-nonce.
  meta?.remove();

  if (!nonce || nonce === PLACEHOLDER) {
    return undefined;
  }
  setNonce(nonce);
  return nonce;
}
